import type {CodeSection, CodeSectionLookup} from '@github-ui/code-nav'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {hpcAttrs} from '@github-ui/hpc'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {Link} from '@primer/react'
import {clsx} from 'clsx'
import React from 'react'

import {useCurrentBlob} from '../../../../hooks/CurrentBlob'
import type {SetStickyLinesType} from '../../../../hooks/use-sticky-lines'
import {numberOfLinesPerContentVisibility} from './CodeLinesNoVirtualization'
import styles from './CodeLinesSSRNoVirtualization.module.css'
import {HighlightedLinesOverlay} from './HighlightedLinesOverlay'
import {HighlightedSymbolsOverlay} from './HighlightedSymbolsOverlay'
import type {CodeLineData} from './hooks/use-code-lines'
import {LineNumberNoVirtualziation} from './LineNumberNoVirtualization'
import {SyntaxHighlightedOverlay} from './SyntaxHighlightedLine'

interface CodeLinesSSRNoVirtualizationProps {
  linesData: CodeLineData[]
  tabSize: number
  onCollapseToggle: () => void
  colorizedLines: SafeHTMLString[] | null
  nonTruncatedLinesData: CodeLineData[]
  codeSections?: Map<number, CodeSection[]>
  codeLineToSectionMap?: CodeSectionLookup
  contentWidth?: number
  copilotAccessAllowed: boolean
  onLineNumberClick?: React.MouseEventHandler<HTMLDivElement>
  onLineStickOrUnstick?: SetStickyLinesType
}

export const CodeLinesSSRNoVirtualization = React.memo(CodeLinesSSRNoVirtualizationUnmemoized)

function CodeLinesSSRNoVirtualizationUnmemoized({
  linesData,
  onLineNumberClick,
  codeSections,
  nonTruncatedLinesData,
  colorizedLines,
  onLineStickOrUnstick,
  tabSize,
  contentWidth,
  copilotAccessAllowed,
  onCollapseToggle,
}: CodeLinesSSRNoVirtualizationProps) {
  const wrapOptionEnabled = useCodeViewOptions().codeWrappingOption.enabled
  const {rawBlobUrl} = useCurrentBlob()
  // Gate `content-visibility: auto` on the SSR line-number wrappers behind a flag
  // so it can be released and validated safely. When enabled, off-screen gutter
  // segments are skipped on first paint (see react-blob.scss).
  const ssrContentVisibilityEnabled = isFeatureEnabled('react_blob_ssr_content_visibility')
  //we want to wrap every 60 lines in a data-visibility wrapper, so we need
  //to make this array to map over down below
  const lineArrayMap = [...Array(Math.floor(linesData.length / numberOfLinesPerContentVisibility) + 1).keys()]

  return (
    <div
      className={clsx(
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        'react-code-file-contents',
        styles.codeFileContents,
        ssrContentVisibilityEnabled && styles.reactBlobSsrContentVisibility,
      )}
      role="presentation"
      aria-hidden
      data-tab-size={tabSize}
      data-paste-markdown-skip
      style={{
        tabSize,
        width: contentWidth,
        maxWidth: wrapOptionEnabled ? '100%' : 'unset',
      }}
      {...hpcAttrs()}
    >
      <div
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        className="react-line-numbers-no-virtualization"
        style={{
          pointerEvents: 'auto',
          position: 'relative',
          zIndex: 2,
        }}
      >
        {lineArrayMap.map(segmentCount => {
          const tempLines = linesData.slice(
            segmentCount * numberOfLinesPerContentVisibility,
            Math.min(
              segmentCount * numberOfLinesPerContentVisibility + numberOfLinesPerContentVisibility,
              linesData.length,
            ),
          )
          return (
            <div
              key={`line-number-wrapper-${segmentCount}-content:${tempLines[0]?.rawText?.substring(0, 100)}`}
              className={styles.reactNoVirtualizationWrapperLinesSsr}
            >
              {tempLines.map(lineData => {
                return (
                  <LineNumberNoVirtualziation
                    codeLineData={lineData}
                    key={`line-number-${lineData.lineNumber}`}
                    onClick={onLineNumberClick}
                    ownedCodeSections={codeSections}
                    onLineStickOrUnstick={onLineStickOrUnstick}
                    onCollapseToggle={onCollapseToggle}
                  />
                )
              })}
            </div>
          )
        })}
      </div>
      {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
      <div className="react-code-lines">
        <HighlightedSymbolsOverlay linesData={linesData} />
        <HighlightedLinesOverlay linesData={linesData} copilotAccessAllowed={copilotAccessAllowed} />
        <SyntaxHighlightedOverlay linesData={nonTruncatedLinesData} colorizedLines={colorizedLines} />
        {linesData.length === 1000 && (
          <div className={styles.viewRemainderLink}>
            <Link href={rawBlobUrl}>View remainder of file in raw view</Link>
          </div>
        )}
      </div>
    </div>
  )
}
